<template>
  <div class="form">
    <div class="post-form">
      <div class="left-col">
        <div v-if="multipleCommunityChoice === false" class="community-name">
          {{ communityList[0].name }}
        </div>
        <div v-else class="input-block">
          <label>
            {{ $t('post.postForm.chooseCommunityToPublish') }}
          </label>
          <v-select :options="communityList"
                    class="v-select-community"
                    :clear-search-on-select="true"
                    :clearable="false"
                    :taggable="false"
                    :max-height="'200px'"
                    label="name"
                    :on-change="onChangeCommunity"
          />
        </div>
        <div class="input-block">
          <label for="post-title">{{ $t('post.postForm.title') }}</label>
          <input id="post-title"
                 data-cy="post-title-input"
                 name="title"
                 v-model="postTitle"
                 class="input"
                 maxlength="128"
          />
          <div class="under-field-container">
            <span v-if="postTitle && postTitle.length > 0">{{ postTitle.length }}/128</span>
          </div>
        </div>
      </div>
      <div class="right-col">
        <ImageSelect :context="'post'"
                     @changeImage="onChangeImage"
                     @removeImage="onRemoveImage"
                     :id-image="idImage"
                     :show-cropper="true"
        />
      </div>
    </div>
    <div class="content">
      <div class="input-block" v-if="users">
        <label for="short-content">{{ $t('post.postForm.content') }}</label>
        <TextEditorWithMetadata id="short-content"
                                :users="users"
                                :on-change="onContentChange"
                                :initial-value="initialPost.content"
                                :initial-metadata="initialPost.metadata"
        />
      </div>
    </div>
    <post-file-select @changeFiles="onChangeFiles"
                      class="select-attachments"
                      v-if="initialPost"
                      :initial-files="initialPost.documents"
    />
    <div class="modal-footer">
      <div class="messg">
        <div class="savingMessage">
          {{ savingMessage }}
        </div>
        <div class="errorMessage">
          {{ errorMessage }}
        </div>
      </div>
      <v-button id="create-post-button"
                data-cy="save-post-button"
                :on-click="editMode ? editPost : createPost"
                class="post-button"
                :disabled="shouldButtonBeDisabled()"
      >
        {{ editMode ? $t('post.postForm.save') : $t('post.postForm.post') }}
      </v-button>
    </div>
  </div>
</template>

<script>
import ImageSelect from '@/components/shared/ImageSelect.vue'
import PostFileSelect from '@/components/post/PostFileSelect'
import VButton from '@/components/shared/VButton'
import VSelect from 'vue-select'
import FormUtils from '../../form-utils'
import TextEditorWithMetadata from '../shared/TextEditor/TextEditorWithMetadata'

export default {
  name: 'PostForm',
  components: { TextEditorWithMetadata, VButton, PostFileSelect, ImageSelect, VSelect },
  props: {
    communityList: {
      type: Array,
      required: true
    },
    multipleCommunityChoice: Boolean,
    initialPost: {
      type: Object,
      default: () => {
        return { title: '', content: '', idImage: '', metadata: null }
      }
    },
    editMode: {
      type: Boolean
    }
  },
  data() {
    return {
      postTitle: '',
      postContent: '',
      postMetadata: null,
      postImage: '',
      savingMessage: '',
      errorMessage: '',
      idCommunity: null,
      files: [],
      disabledButton: false,
      formImageId: ''
    }
  },
  created() {
    if (this.multipleCommunityChoice === false) {
      this.onChangeCommunity(this.communityList[0])
    }
    this.initialPost.content = this.$sanitizeHTML(this.$decodeHTML(this.initialPost.content))

    this.postTitle = this.initialPost.title
    this.postContent = this.initialPost.content
    this.postMetadata = this.initialPost.metadata
    this.formImageId = this.initialPost.idImage
    this.files = this.initialPost.documents ? this.initialPost.documents : []
  },
  methods: {
    async isFormValid() {
      const result = await this.$validator.validateAll()
      return !!result
    },
    onChangeImage(image) {
      this.image = image
    },
    onRemoveImage() {
      this.formImageId = null
      this.image = null
    },
    onChangeFiles(files) {
      this.files = files
    },
    onContentChange(html, metadata) {
      this.postContent = html
      this.postMetadata = metadata
    },
    onChangeCommunity(selectedCommunity) {
      if (selectedCommunity.type === 'public') {
        this.$store.dispatch('searchUsersByPublicCommunity', selectedCommunity.id)
      } else {
        this.$store.dispatch('searchUsersByCommunity', selectedCommunity.id)
      }
      this.idCommunity = selectedCommunity.id
    },
    async createPost() {
      if (!await this.isFormValid()) {
        return
      }
      this.errorMessage = ''
      this.savingMessage = this.$t('post.postForm.saving')
      this.disabledButton = true

      let formData = new FormData()
      this.files.forEach((file) => {
        FormUtils.appendFile(formData, 'attachments', file, file.name)
      })
      if (this.image) {
        FormUtils.append(formData, 'image', this.image.data)
        FormUtils.append(formData, 'imageName', this.image.filename)
      }
      FormUtils.append(formData, 'title', this.postTitle)
      FormUtils.append(formData, 'content', this.postContent)
      FormUtils.append(formData, 'idCommunity', this.idCommunity)
      if (this.postMetadata) {
        FormUtils.append(formData, 'metadata', JSON.stringify(this.postMetadata))
      }
      const result = await this.$store.dispatch('createPost', formData)
      if (result.status === 201) {
        this.savingMessage = ''
        this.$emit('close')
      } else {
        this.showErrorMessage()
      }
    },
    async editPost() {
      if (!(await this.isFormValid())) {
        return
      }
      this.errorMessage = ''
      this.savingMessage = this.$t('post.postForm.saving')

      let formData = new FormData()
      FormUtils.append(formData, 'title', this.postTitle)
      FormUtils.append(formData, 'content', this.postContent)
      if (this.postMetadata) {
        FormUtils.append(formData, 'metadata', JSON.stringify(this.postMetadata))
      }
      if (this.formImageId) {
        FormUtils.append(formData, 'imageId', this.formImageId)
      }
      if (this.image && this.image.data) {
        FormUtils.append(formData, 'image', this.image.data)
        FormUtils.append(formData, 'imageName', this.image.filename)
      }
      this.files.forEach((file) => {
        if (file instanceof File) {
          FormUtils.appendFile(formData, 'attachments', file, file.name)
        } else {
          // only meta files
          FormUtils.append(formData, 'existing-attachments', file.id)
        }
      })

      const result = await this.$store.dispatch('editPost', { postId: this.initialPost.id, formData: formData })
      if (result.status === 200) {
        this.savingMessage = ''
        this.$emit('close')
      } else {
        this.showErrorMessage()
      }
    },
    shouldButtonBeDisabled() {
      return (!this.postTitle && !this.postContent && !this.postImage) ||
          !this.idCommunity ||
          this.disabledButton
    },
    showErrorMessage() {
      this.savingMessage = ''
      this.errorMessage = this.$t('post.postForm.somethingWentWrong')
      this.disabledButton = false
    }
  },
  computed: {
    idImage() {
      return this.initialPost.idImage
    },
    users() {
      if (this.idCommunity) {
        return this.$store.getters.userByCommunity[this.idCommunity]
      }
      return undefined
    }
  }
}
</script>

<style scoped lang="scss">

#short-content {
  width: 100%;
}

/* Customize the label (the container) */
.form {
  width: 100%;
  max-height: 713px;
  overflow-y: auto;
  overflow-x: hidden;
  @media (max-width: $header-change) {
    max-width: 600px;
    margin: auto;
    height: calc(100vh - 140px);
    max-height: none;
    display: block;
  }

  .post-form {
    display: flex;
    justify-content: space-between;
    padding: 24px;
    width: calc(100% - 48px);

    @media (max-width: $header-change) {
      flex-direction: column;
      justify-content: center;
      //height: calc(100% - 300px);

      .left-col {
        width: 100%;
      }

      .right-col{
          display: flex;
          align-items: center;
          justify-content: center;
      }
    }
  }

  .content {
    padding: 0 24px 24px;
    @media (max-width: $header-change) {
      padding: 0 0 0 20px;
    }
  }

  .v-select-community {
    background: #FFFFFF;
    width: 367px;
    height: 32px;
    max-width: 100%;
    &.error {
      border: 1px solid $cerise-color;
    }
    @media (max-width: $header-change) {
      width: 100%
    }
  }

  .select-attachments {
    padding: 0 24px 20px 24px;
  }
}

.community-name {
  font-family: OpenSans-Regular, sans-serif;
  font-size: 16px;
  color: $tertiary-color;
  line-height: 24px;
  margin-bottom: 20px;
}

.input-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 13px;
  letter-spacing: 0;
  line-height: 19px;
  color: $black-color;
  margin-bottom: 20px;

  label {
    width: 100%;
    margin-bottom: 5px;
    text-align: initial;
  }

  @media (max-width: $header-change) {
    display: flex;
    align-items: flex-start;
    width: 100%
  }

    .under-field-container {
    height: 1.2rem;
    justify-content: flex-end;
    display: flex;
  }

  .input {
    background: #ffffff;
    border: 1px solid #d8d8d8;
    border-radius: 3px;
    min-height: 31px;
    max-width: 100%;

    @media (max-width: $header-change) {
      padding: 0;
        width: 100%;
    }

    &.error {
      border: 1px solid $cerise-color;
    }
  }

  input {
    padding: 0 10px;
    width: 345px;
    height: 32px;
  }
}

.modal-footer {
  box-shadow: inset 0 1px 0 0 #d8d8d8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  padding: 10px 24px;

  @media (max-width: $header-change) {
    position: absolute;
    bottom: 0;
    right: 0;
    width: calc(100% - 40px);
  }

  .messg {
    display: flex;

    .savingMessage {
      margin: 24px 0 24px 24px;
      color: $primary-color;
    }

    .errorMessage {
      margin: 24px 0 24px 0;
      color: $cerise-color;
    }
  }
}
</style>
